/**
 * TenantFilters Component
 *
 * Provides search and filter controls for the tenant list:
 * - Search input
 * - Status filter dropdown
 * - Isolation level filter dropdown
 * - Health filter dropdown
 *
 * @module features/tenants/components/TenantFilters
 */

import { SearchIcon } from '../../../../components/common/Icons';
import type { UseTenantFilters } from '../../hooks/useTenants';

interface TenantFiltersProps {
  /** Current filter values */
  filters: UseTenantFilters;
  /** Update filter values (partial update supported) */
  setFilters: (filters: Partial<UseTenantFilters>) => void;
  /** Selected health filter value */
  selectedHealth: string;
  /** Update selected health filter */
  setSelectedHealth: (health: string) => void;
}

/**
 * Renders search and filter controls for the tenant list.
 */
export const TenantFilters: React.FC<TenantFiltersProps> = ({
  filters,
  setFilters,
  selectedHealth,
  setSelectedHealth,
}) => {
  return (
    <div className="page-toolbar">
      <div className="input-wrapper" style={{ maxWidth: '320px' }}>
        <SearchIcon size={18} className="input-icon" />
        <input
          type="text"
          className="input input-with-icon"
          placeholder="Search tenants..."
          value={filters.search}
          onChange={(e) => setFilters({ search: e.target.value })}
        />
      </div>
      <select
        className="select"
        value={filters.status}
        onChange={(e) => setFilters({ status: e.target.value })}
      >
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="pending">Pending</option>
        <option value="suspended">Suspended</option>
        <option value="provisioning">Provisioning</option>
        <option value="archived">Archived</option>
      </select>
      <select
        className="select"
        value={filters.isolationLevel}
        onChange={(e) => setFilters({ isolationLevel: e.target.value })}
      >
        <option value="all">All Isolation Levels</option>
        <option value="row">Row-Level</option>
        <option value="schema">Schema</option>
        <option value="container">Container</option>
      </select>
      <select
        className="select"
        value={selectedHealth}
        onChange={(e) => setSelectedHealth(e.target.value)}
      >
        <option value="all">All Health</option>
        <option value="healthy">Healthy</option>
        <option value="not_provisioned">Not Provisioned</option>
        <option value="stopped">Stopped</option>
        <option value="error">Error</option>
      </select>
    </div>
  );
};
